import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import { useFetchList } from "../../hooks/useFetchList";
import { ensureChartSetup } from "./ChartSetup";
import { inferFirstKey } from "../../api";

ensureChartSetup();

export default function SalesByDateChart({ headers = {} }) {
  const { data, loading, error } = useFetchList("/v1/frontend/salesByDate", {
    headers,
  });

  const chart = useMemo(() => {
    if (!data || data.length === 0) return null;
    const keys = Object.keys(data[0] || {});
    const dateKey = inferFirstKey(keys, [/(date|day)$/i]);
    const valueKey = inferFirstKey(
      keys.filter((k) => k !== dateKey),
      [/(sales|amount|total)/i]
    );
    const parseDateValue = (v) => {
      if (v == null) return null;
      if (v instanceof Date) return v;
      if (typeof v === "number" && Number.isFinite(v)) return new Date(v);
      const s = String(v).trim();
      const dm = /^\d{2}\/\d{2}\/\d{4}$/.test(s);
      if (dm) {
        const [dd, mm, yyyy] = s.split("/").map(Number);
        return new Date(yyyy, mm - 1, dd);
      }
      if (/^\d{4}-\d{2}-\d{2}/.test(s)) return new Date(s);
      const d = new Date(s);
      return isNaN(d.getTime()) ? null : d;
    };

    const points = data
      .map((r) => {
        const x = parseDateValue(r[dateKey]);
        return { x, y: Number(r[valueKey]) || 0 };
      })
      .filter((p) => p.x != null);
    if (points.length === 0) return null;
    const allZero = points.every((p) => p.y === 0);
    return {
      _allZero: allZero,
      datasets: [
        {
          label: "Sales",
          data: points,
          borderColor: "#4CAF50",
          backgroundColor: "transparent",
          fill: false,
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: "#4CAF50",
          pointBorderColor: "#ffffff",
          pointBorderWidth: 2,
          borderWidth: 3,
        },
      ],
    };
  }, [data]);

  if (loading) return <div style={{ textAlign: "center" }}>Loading...</div>;
  if (error) return <div style={{ color: "#d33" }}>Failed to load.</div>;
  if (!chart) return <div>No data.</div>;
  if (chart._allZero)
    return (
      <div style={{ fontSize: 12, opacity: 0.7 }}>
        All values are zero for the selected range.
      </div>
    );

  return (
    <div
      style={{
        width: "100%",
        minHeight: 220,
        height: "30vw",
        maxHeight: 340,
        boxSizing: "border-box",
      }}
    >
      <Line
        data={chart}
        options={{
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: "rgba(0,0,0,0.8)",
              titleColor: "#fff",
              bodyColor: "#fff",
              borderColor: "#4CAF50",
              borderWidth: 1,
              callbacks: {
                label: function (context) {
                  return `Sales: $${context.parsed.y.toLocaleString()}`;
                },
              },
            },
          },
          maintainAspectRatio: false,
          scales: {
            x: {
              type: "time",
              time: { unit: "day", tooltipFormat: "yyyy-MM-dd" },
              ticks: {
                maxRotation: 0,
                minRotation: 0,
                font: { size: 12 },
              },
              grid: {
                display: true,
                color: "rgba(0,0,0,0.1)",
              },
              border: {
                display: false,
              },
            },
            y: {
              ticks: {
                font: { size: 12 },
                color: "#666",
                callback: function (value) {
                  return "$" + value.toLocaleString();
                },
              },
              grid: {
                display: true,
                color: "rgba(0,0,0,0.1)",
              },
              border: {
                display: false,
              },
            },
          },
          interaction: {
            intersect: false,
            mode: "index",
          },
        }}
      />
    </div>
  );
}
